// ═══════════════════════════════════════════════════════════════════
//  SearchScopeChip — Small popover for scope selection.
// ═══════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useSearch } from '@/contexts/SearchContext';
import { useTranslation } from '@/i18n/LanguageContext';
import type { SearchScope } from '@/types/search';

const ALL_SCOPES: SearchScope[] = ['all', 'workflows', 'atlas'];

export function SearchScopeChip() {
  const { t } = useTranslation();
  const { scope, setScope } = useSearch();
  const [open, setOpen] = useState(false);

  const labelFor = (s: SearchScope): string =>
    s === 'all'
      ? t('searchCanvasScopeAll')
      : s === 'workflows'
        ? t('searchCanvasScopeWorkflows')
        : s === 'atlas'
          ? t('searchCanvasScopeAtlas')
          : s;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-9 gap-1.5 px-2 text-xs"
          aria-label="Search scope"
        >
          {labelFor(scope)}
          <ChevronDown size={12} />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-40 p-1" sideOffset={4}>
        {ALL_SCOPES.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => {
              setScope(s);
              setOpen(false);
            }}
            className="flex w-full items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
          >
            <span>{labelFor(s)}</span>
            {scope === s && <Check size={14} />}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
